import { useEffect, useState } from "react";
import axios from "axios";
import { Phone, Mail, Globe, MapPin, MessageCircle, Send, CheckCircle2, AlertTriangle } from "lucide-react";
import { CONTACT, BUSINESS_TYPES, SERVICE_OPTIONS } from "../../data/content";
import { ChapterHeading } from "./ChapterHeading";
import { Reveal } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const PHONE_RE = /^(\+91[\s-]?)?[6-9]\d{9}$/;

const initialForm = {
    name: "",
    businessName: "",
    phone: "",
    email: "",
    businessType: "",
    service: "",
    details: "",
};

const validate = (f) => {
    const e = {};
    if (!f.name.trim()) e.name = "Please enter your name";
    if (!PHONE_RE.test(f.phone.trim())) e.phone = "Enter a valid 10-digit mobile number";
    if (!EMAIL_RE.test(f.email.trim())) e.email = "Enter a valid email address";
    if (!f.businessType) e.businessType = "Select your business type";
    if (!f.service) e.service = "Select a service";
    if (f.details.trim().length < 10) e.details = "Tell us a little more (min 10 characters)";
    return e;
};

const inputCls = (err) =>
    `w-full rounded-xl border bg-[#0A0E17] px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-[#F5C518]/70 ${
        err ? "border-red-500/60" : "border-white/10"
    }`;

const Field = ({ label, error, children, optional }) => (
    <label className="block">
        <span className="mb-1.5 flex items-baseline gap-2 text-xs font-medium text-slate-300">
            {label}
            {optional && <span className="text-[10px] text-slate-600">(optional)</span>}
        </span>
        {children}
        {error && (
            <span data-testid={`form-error-${label.toLowerCase().replace(/\s+/g, "-")}`} className="mt-1 block text-[11px] text-red-400">
                {error}
            </span>
        )}
    </label>
);

export const Contact = () => {
    const [form, setForm] = useState(initialForm);
    const [errors, setErrors] = useState({});
    const [done, setDone] = useState(false);
    const [sending, setSending] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [options, setOptions] = useState(SERVICE_OPTIONS);

    useEffect(() => {
        const handler = (e) => {
            const val = e.detail;
            setOptions((prev) => (prev.includes(val) ? prev : [...prev, val]));
            setForm((f) => ({ ...f, service: val }));
            setDone(false);
        };
        window.addEventListener("prefill-service", handler);
        return () => window.removeEventListener("prefill-service", handler);
    }, []);

    const set = (k) => (e) => {
        setForm((f) => ({ ...f, [k]: e.target.value }));
        setErrors((er) => ({ ...er, [k]: undefined }));
    };

    const waMessage = () =>
        `${CONTACT.waBase}?text=` +
        encodeURIComponent(
            `Hello Animexa Tech Solutions, I would like to discuss a digital solution for my business.\n\nName: ${form.name}\nBusiness: ${form.businessName || "-"}\nPhone: ${form.phone}\nEmail: ${form.email}\nBusiness Type: ${form.businessType}\nService Required: ${form.service}\nProject Details: ${form.details}`
        );

    const mailLink = () =>
        `mailto:${CONTACT.email}?subject=` +
        encodeURIComponent(`Enquiry — ${form.service} (${form.name})`) +
        `&body=` +
        encodeURIComponent(
            `Name: ${form.name}\nBusiness: ${form.businessName || "-"}\nPhone: ${form.phone}\nEmail: ${form.email}\nBusiness Type: ${form.businessType}\nService Required: ${form.service}\n\nProject Details:\n${form.details}`
        );

    const onSubmit = async (e) => {
        e.preventDefault();
        const errs = validate(form);
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;
        setSending(true);
        setSubmitError("");
        try {
            await axios.post(`${API}/enquiry`, {
                name: form.name.trim(),
                business_name: form.businessName.trim(),
                phone: form.phone.trim(),
                email: form.email.trim(),
                business_type: form.businessType,
                service: form.service,
                details: form.details.trim(),
            });
            setDone(true);
        } catch (err) {
            setSubmitError(
                "We couldn't send your enquiry right now. Please try again in a moment, or reach us directly on WhatsApp or email below."
            );
        } finally {
            setSending(false);
        }
    };

    const info = [
        { icon: Phone, label: "Phone", value: CONTACT.phoneDisplay, href: CONTACT.phoneTel, testid: "contact-phone" },
        { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, testid: "contact-email" },
        { icon: Globe, label: "Website", value: CONTACT.website, href: "https://animexa.in", testid: "contact-website" },
        { icon: MapPin, label: "Location", value: CONTACT.location, href: null, testid: "contact-location" },
    ];

    return (
        <section id="contact" data-testid="contact-section" className="py-24 lg:py-32 relative overflow-hidden">
            <div
                className="absolute -top-24 right-0 h-[24rem] w-[24rem] rounded-full bg-[#00E5FF]/7 blur-[110px]"
                aria-hidden="true"
            />
            <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto relative">
                <ChapterHeading
                    label="Contact Us"
                    title="Let's Build Something Amazing"
                    sub="Tell us about your business and what you want to build — we'll get back with a practical plan and transparent pricing."
                />

                <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
                    <Reveal className="lg:col-span-2">
                        <div className="space-y-4">
                            {info.map((it) => {
                                const Inner = (
                                    <>
                                        <div className="h-11 w-11 rounded-xl bg-[#00E5FF]/10 border border-[#00E5FF]/25 flex items-center justify-center shrink-0">
                                            <it.icon size={19} className="text-[#00E5FF]" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-jbmono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                                                {it.label}
                                            </p>
                                            <p className="text-sm sm:text-base text-white mt-0.5 break-all">
                                                {it.value}
                                            </p>
                                        </div>
                                    </>
                                );
                                return it.href ? (
                                    <a
                                        key={it.label}
                                        data-testid={it.testid}
                                        href={it.href}
                                        target={it.href.startsWith("http") ? "_blank" : undefined}
                                        rel={it.href.startsWith("http") ? "noopener noreferrer" : undefined}
                                        className="flex items-center gap-4 rounded-2xl border border-white/8 bg-[#0D131F] p-4 transition-all duration-300 hover:border-[#00E5FF]/40 hover:-translate-y-0.5"
                                    >
                                        {Inner}
                                    </a>
                                ) : (
                                    <div
                                        key={it.label}
                                        data-testid={it.testid}
                                        className="flex items-center gap-4 rounded-2xl border border-white/8 bg-[#0D131F] p-4"
                                    >
                                        {Inner}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-6 flex flex-wrap gap-3">
                            <a
                                data-testid="contact-call-btn"
                                href={CONTACT.phoneTel}
                                className="inline-flex items-center gap-2 rounded-full bg-[#F5C518] px-5 py-3 text-sm font-semibold text-[#07090E] transition-all duration-300 hover:bg-[#E5B50F] active:scale-95"
                            >
                                <Phone size={15} /> Call Now
                            </a>
                            <a
                                data-testid="contact-whatsapp-btn"
                                href={CONTACT.waDefault}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 px-5 py-3 text-sm font-medium text-emerald-300 transition-all duration-300 hover:bg-emerald-400/10 active:scale-95"
                            >
                                <MessageCircle size={15} /> WhatsApp
                            </a>
                            <a
                                data-testid="contact-email-btn"
                                href={`mailto:${CONTACT.email}`}
                                className="inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/40 px-5 py-3 text-sm font-medium text-[#00E5FF] transition-all duration-300 hover:bg-[#00E5FF]/10 active:scale-95"
                            >
                                <Mail size={15} /> Email
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={0.1} className="lg:col-span-3">
                        <form
                            data-testid="contact-form"
                            onSubmit={onSubmit}
                            noValidate
                            className="rounded-3xl border border-white/10 bg-[#0D131F] p-6 sm:p-8 space-y-5"
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                                <Field label="Name" error={errors.name}>
                                    <input
                                        data-testid="form-name"
                                        className={inputCls(errors.name)}
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Your full name"
                                    />
                                </Field>
                                <Field label="Business Name" optional>
                                    <input
                                        data-testid="form-business-name"
                                        className={inputCls(false)}
                                        value={form.businessName}
                                        onChange={set("businessName")}
                                        placeholder="Your business name"
                                    />
                                </Field>
                                <Field label="Phone" error={errors.phone}>
                                    <input
                                        data-testid="form-phone"
                                        className={inputCls(errors.phone)}
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder="10-digit mobile number"
                                        inputMode="tel"
                                    />
                                </Field>
                                <Field label="Email" error={errors.email}>
                                    <input
                                        data-testid="form-email"
                                        className={inputCls(errors.email)}
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="you@example.com"
                                        inputMode="email"
                                    />
                                </Field>
                                <Field label="Business Type" error={errors.businessType}>
                                    <select
                                        data-testid="form-business-type"
                                        className={inputCls(errors.businessType)}
                                        value={form.businessType}
                                        onChange={set("businessType")}
                                    >
                                        <option value="" disabled>
                                            Select business type
                                        </option>
                                        {BUSINESS_TYPES.map((b) => (
                                            <option key={b} value={b}>
                                                {b}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                                <Field label="Service Required" error={errors.service}>
                                    <select
                                        data-testid="form-service"
                                        className={inputCls(errors.service)}
                                        value={form.service}
                                        onChange={set("service")}
                                    >
                                        <option value="" disabled>
                                            Select a service
                                        </option>
                                        {options.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </Field>
                            </div>
                            <Field label="Project Details" error={errors.details}>
                                <textarea
                                    data-testid="form-details"
                                    rows={4}
                                    className={`${inputCls(errors.details)} resize-none`}
                                    value={form.details}
                                    onChange={set("details")}
                                    placeholder="Briefly describe what you want to build…"
                                />
                            </Field>

                            {done ? (
                                <div
                                    data-testid="form-success-panel"
                                    className="rounded-2xl border border-emerald-400/35 bg-emerald-400/5 p-5"
                                >
                                    <p className="flex items-start gap-2.5 text-sm text-slate-200 leading-relaxed">
                                        <CheckCircle2 size={17} className="mt-0.5 shrink-0 text-emerald-400" />
                                        Your enquiry has been sent successfully. Thank you — the
                                        Animexa team has received your details and will get back to
                                        you shortly.
                                    </p>
                                    <div className="mt-4 flex flex-wrap gap-3">
                                        <a
                                            data-testid="form-send-whatsapp"
                                            href={waMessage()}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 px-5 py-2.5 text-sm font-medium text-emerald-300 transition-colors hover:bg-emerald-400/10"
                                        >
                                            <MessageCircle size={15} /> Also continue on WhatsApp
                                        </a>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {submitError && (
                                        <div
                                            data-testid="form-error-panel"
                                            className="rounded-2xl border border-red-500/40 bg-red-500/5 p-5"
                                        >
                                            <p className="flex items-start gap-2.5 text-sm text-slate-200 leading-relaxed">
                                                <AlertTriangle size={17} className="mt-0.5 shrink-0 text-red-400" />
                                                {submitError}
                                            </p>
                                            <div className="mt-4 flex flex-wrap gap-3">
                                                <a
                                                    data-testid="form-fallback-whatsapp"
                                                    href={waMessage()}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 rounded-full bg-[#F5C518] px-5 py-2.5 text-sm font-semibold text-[#07090E] transition-colors hover:bg-[#E5B50F]"
                                                >
                                                    <MessageCircle size={15} /> Send via WhatsApp
                                                </a>
                                                <a
                                                    data-testid="form-fallback-email"
                                                    href={mailLink()}
                                                    className="inline-flex items-center gap-2 rounded-full border border-[#00E5FF]/40 px-5 py-2.5 text-sm font-medium text-[#00E5FF] transition-colors hover:bg-[#00E5FF]/10"
                                                >
                                                    <Mail size={15} /> Send via Email
                                                </a>
                                            </div>
                                        </div>
                                    )}
                                    <button
                                        data-testid="form-submit-btn"
                                        type="submit"
                                        disabled={sending}
                                        className="group inline-flex w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-[#F5C518] px-8 py-3.5 text-sm font-semibold text-[#07090E] transition-all duration-300 hover:bg-[#E5B50F] hover:shadow-[0_0_30px_-6px_rgba(245,197,24,0.5)] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {sending ? "Sending…" : "Send Enquiry"}
                                        {!sending && (
                                            <Send size={15} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                                        )}
                                    </button>
                                </>
                            )}
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
